import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark';

/** Supported UI languages. `ru` is the source-of-truth default. */
export type Language = 'ru' | 'uz' | 'en';
export const LANGUAGES: readonly Language[] = ['ru', 'uz', 'en'];

/** Short switcher labels for each language, as the account menus render them. */
export const LANGUAGE_LABEL: Record<Language, string> = {
  ru: 'Ру',
  uz: 'Uz',
  en: 'En',
};

/**
 * Ephemeral UI state ONLY (see CLAUDE.md state-boundary table). Server state
 * belongs to TanStack Query, URL state to the router, form state to RHF.
 * Durable prefs (theme, sidebar) are persisted; transient flags are not.
 */
interface UiState {
  theme: Theme;
  language: Language;
  sidebarCollapsed: boolean;
  commandPaletteOpen: boolean;
  /**
   * The branch (restaurant) the merchant is currently viewing. A durable
   * preference, not server state — the branch LIST lives in TanStack Query;
   * only the id of the chosen one is remembered here. `null` means "not yet
   * chosen", which resolves to the merchant's first branch at read time.
   */
  activeBranchId: string | null;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  setLanguage: (language: Language) => void;
  toggleSidebar: () => void;
  setCommandPaletteOpen: (open: boolean) => void;
  setActiveBranchId: (id: string | null) => void;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: 'light',
      language: 'ru',
      sidebarCollapsed: false,
      commandPaletteOpen: false,
      activeBranchId: null,
      toggleTheme: () => set((s) => ({ theme: s.theme === 'dark' ? 'light' : 'dark' })),
      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => set({ language }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setCommandPaletteOpen: (commandPaletteOpen) => set({ commandPaletteOpen }),
      setActiveBranchId: (activeBranchId) => set({ activeBranchId }),
    }),
    {
      name: 'ui-prefs',
      // Persist only durable prefs; never persist transient flags.
      partialize: (s) => ({
        theme: s.theme,
        language: s.language,
        sidebarCollapsed: s.sidebarCollapsed,
        activeBranchId: s.activeBranchId,
      }),
    },
  ),
);
